import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { ApiProvider } from "@src/store/ApiProvider";
import { withPasswordManagerRefresh } from "./passwordManagerRequest";

export interface IAddCredentialParams {
	companyId: number;
	vendorId: number;
	username: string;
	password: string;
	notes: string;
	notificationEmail: string;
	crawlerId?: number | null;
	teamCode?: string | null;
}

export interface IAddCredentialResponseItem {
	credentialId: number;
	companyName: string;
	companyId: number;
	vendorName: string;
	vendorId: number;
	username: string;
	notes: string;
	teamCode?: string | null;
	loginLock?: boolean;
	verificationCode?: string | null;
	lastUpdatedDate?: string | null;
	lastUpdatedUser?: string | null;
	errorMessage?: string;
	created?: boolean;
	updated?: boolean;
	syncAccountId?: number | null;
	tradeDeskAccountCreatedDate?: string | null;
	tradeDeskAccountUpdatedDate?: string | null;
	lastVerifiedDate?: string | null;
	isValid?: boolean | null;
	notificationEmail?: string | null;
}

interface IAddCredentialState {
	loading: boolean;
	error: string | null;
}

const initialState: IAddCredentialState = {
	loading: false,
	error: null,
};

const getAuthHeaders = () => {
	const token = localStorage.getItem("PasswordManager_accessToken") ?? "";
	return {
		Authorization: token ? `Bearer ${token}` : undefined,
		passwordManagerAccessToken: token || undefined,
		"Content-Type": "application/json",
	};
};

const buildUrl = () => {
	const baseUrl = ApiProvider.default.passwordManagerBaseURL || "https://qa.stagefront.com/password-manager/api";
	return `${baseUrl.replace(/\/$/, "")}/Credentials`;
};

export const addNewCredential = createAsyncThunk<IAddCredentialResponseItem, IAddCredentialParams>(
	"passwordManager/addNewCredential",
	async (payload, { rejectWithValue, dispatch }) => {
		try {
			const body = [
				{
					companyId: payload.companyId,
					vendorId: payload.vendorId,
					username: payload.username,
					password: payload.password,
					notes: payload.notes,
					crawlerId: payload.crawlerId ?? null,
					teamCode: payload.teamCode ?? null,
					errorMessage: "",
					NotificationEmail: payload.notificationEmail,
				},
			];

			const res = await withPasswordManagerRefresh(dispatch, () =>
				axios.post<Array<IAddCredentialResponseItem>>(buildUrl(), body, { headers: getAuthHeaders() }),
			);
			const data = res.data ?? [];
			if (!Array.isArray(data) || !data.length) {
				throw new Error("Unable to parse credential response");
			}
			return data[0];
		} catch (error: unknown) {
			const message = error instanceof Error ? error.message : "Failed to create credential";
			return rejectWithValue(message);
		}
	},
);

const addNewCredentialSlice = createSlice({
	name: "passwordManager/addNewCredential",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(addNewCredential.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(addNewCredential.fulfilled, (state) => {
				state.loading = false;
				state.error = null;
			})
			.addCase(addNewCredential.rejected, (state, action) => {
				state.loading = false;
				state.error = (action.payload as string) || action.error.message || "Failed to create credential";
			});
	},
});

export const addNewCredentialReducer = addNewCredentialSlice.reducer;
